// src/features/Planning/components/Step19AttachRegistryCopy.jsx
import {
  Box,
  Text,
  Flex,
  Button,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function StepAttachPlotImage({
  formData,
  setFormData,
  onNext,
  onBack,
  isLastStep,
  onSubmit,
  projectId,
  token,
  updatedBy,
  viewMode,
}) {
  const plotFileInputRef = useRef();
  const refFileInputRef = useRef();

  const [isUploadingPlot, setIsUploadingPlot] = useState(false);
  const [isUploadingRef, setIsUploadingRef] = useState(false);
  const [plotUploadError, setPlotUploadError] = useState("");
  const [refUploadError, setRefUploadError] = useState("");

  // ── Existing values from formData ─────────────────────────────────────────
  const plotFile = formData?.plotFile ?? null;
  const refFile = formData?.refFile ?? null;

  const existingPlotUri = Array.isArray(formData?.attachmentUri)
    ? formData.attachmentUri.length > 0
    : !!formData?.attachmentUri;

  const existingRefUri = Array.isArray(formData?.referenceImage)
    ? formData.referenceImage.length > 0
    : !!formData?.referenceImage;

  // ── File select handlers ───────────────────────────────────────────────────
  const handlePlotFile = (e) => {
    if (viewMode) return;
    const selected = e.target.files?.[0] || null;
    setFormData({ ...formData, plotFile: selected, attachmentUri: "" });
    setPlotUploadError("");
  };

  const handleRefFile = (e) => {
    if (viewMode) return;
    const selected = e.target.files?.[0] || null;
    setFormData({ ...formData, refFile: selected, referenceImage: "" });
    setRefUploadError("");
  };

  const handlePlotDrop = (e) => {
    e.preventDefault();
    if (viewMode) return;
    const dropped = e.dataTransfer.files?.[0] || null;
    setFormData({ ...formData, plotFile: dropped, attachmentUri: "" });
    setPlotUploadError("");
  };

  const handleRefDrop = (e) => {
    e.preventDefault();
    if (viewMode) return;
    const dropped = e.dataTransfer.files?.[0] || null;
    setFormData({ ...formData, refFile: dropped, referenceImage: "" });
    setRefUploadError("");
  };

  // ── Upload helper ──────────────────────────────────────────────────────────
  const uploadFile = async (fileToUpload, setUploading, setError) => {
    if (!fileToUpload || !projectId || viewMode) return null;
    setUploading(true);
    setError("");
    try {
      const body = new FormData();
      const prefix = updatedBy === "admin" ? "internal" : "customer";
      body.append("category", "3");
      if (prefix === "internal") {
        body.append("media[]", fileToUpload);
      } else {
        body.append("media", fileToUpload);
      }
      const response = await fetch(
        `${API_BASE}/${prefix}/pm/addAttachment/${projectId}`,
        { method: "POST", headers: { "x-api-key": token }, body }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }
      const data = await response.json();
      console.log("Upload Success:", data);
      return data.uri || null;
    } catch (err) {
      console.error("Upload Error:", err);
      setError(err.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // ── Next handler ───────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (viewMode) {
      if (isLastStep) onSubmit(formData);
      else onNext(formData);
      return;
    }

    let updatedData = { ...formData };
    let hasError = false;

    // Upload plot image if new file selected
    if (plotFile && !formData.attachmentUri) {
      const uri = await uploadFile(plotFile, setIsUploadingPlot, setPlotUploadError);
      if (!uri) { hasError = true; }
      else { updatedData = { ...updatedData, attachmentUri: uri }; }
    }

    // Upload reference image if new file selected
    if (!hasError && refFile && !formData.referenceImage) {
      const uri = await uploadFile(refFile, setIsUploadingRef, setRefUploadError);
      if (!uri) { hasError = true; }
      else { updatedData = { ...updatedData, referenceImage: uri }; }
    }

    if (hasError) return;

    // attachPlotImage flag: yes agar koi bhi file/uri ho
    const hasAnyAttachment =
      updatedData.attachmentUri ||
      (Array.isArray(updatedData.attachmentUri) && updatedData.attachmentUri.length > 0);
    updatedData.attachPlotImage = hasAnyAttachment ? "yes" : "no";

    setFormData(updatedData);
    if (isLastStep) onSubmit(updatedData);
    else onNext(updatedData);
  };

  const isUploading = isUploadingPlot || isUploadingRef;
  const isNextDisabled = isUploading;

  // ── Upload Zone Component (inline) ────────────────────────────────────────
  const UploadZone = ({
    label,
    subLabel,
    icon,
    file: currentFile,
    existingUri: hasExisting,
    existingUriValue,
    isUploading: uploading,
    uploadError: error,
    inputRef,
    onFileChange,
    onDrop,
    accentColor = "cyan",
  }) => (
    <Box>
      {/* Zone label */}
      <Flex align="center" gap={2} mb={2}>
        <Text fontSize="14px" fontWeight="600" color="gray.700">{icon} {label}</Text>
        <Text fontSize="11px" color="gray.400" fontWeight="400">{subLabel}</Text>
      </Flex>

      <Box
        border="1.5px dashed"
        borderColor={error ? "red.400" : uploading ? `${accentColor}.500` : `${accentColor}.300`}
        borderRadius="12px"
        p={4}
        display="flex"
        alignItems="center"
        flexDirection="column"
        bg={error ? "red.50" : uploading ? `${accentColor}.50` : viewMode ? "gray.100" : "gray.50"}
        textAlign="center"
        cursor={uploading || viewMode ? "default" : "pointer"}
        minH="110px"
        onClick={() => !uploading && !viewMode && inputRef.current?.click()}
        onDrop={viewMode ? (e) => e.preventDefault() : onDrop}
        onDragOver={(e) => e.preventDefault()}
        transition="all 0.15s"
        _hover={!uploading && !viewMode ? { borderColor: `${accentColor}.500`, bg: `${accentColor}.50` } : {}}
      >
        {uploading ? (
          <Flex direction="column" align="center" gap={2}>
            <Spinner size="md" color={`${accentColor}.500`} thickness="3px" />
            <Text fontSize="13px" color={`${accentColor}.600`} fontWeight="500">Uploading...</Text>
          </Flex>
        ) : (
          <>
            <Text fontSize="32px" mb={1}>📁</Text>
            <Text fontSize="13px" color="gray.600" fontWeight="500">
              {viewMode ? "View only — upload disabled" : "Tap to browse or drag & drop"}
            </Text>
            {!viewMode && (
              <Text fontSize="11px" color="gray.400" mt={0.5}>Images, PDF supported</Text>
            )}

            <input
              type="file"
              ref={inputRef}
              style={{ display: "none" }}
              onChange={onFileChange}
              accept="image/*,.pdf"
              disabled={viewMode}
            />

            {/* Newly selected file */}
            {currentFile && (
              <Box mt={2} px={3} py={1.5} bg="white" borderRadius="6px"
                border="1px solid" borderColor={`${accentColor}.300`}>
                <Text color={`${accentColor}.700`} fontSize="12px" fontWeight="500">
                  📄 {currentFile.name}
                </Text>
                {formData[accentColor === "cyan" ? "attachmentUri" : "referenceImage"] && (
                  <Text color="green.600" fontSize="11px" mt={0.5}>✓ Uploaded</Text>
                )}
              </Box>
            )}

            {/* Already uploaded (prefill) */}
            {!currentFile && hasExisting && (
              <Box mt={2} px={3} py={1.5} bg="green.50" borderRadius="6px"
                border="1px solid" borderColor="green.300">
                <Text color="green.700" fontSize="12px" fontWeight="500">✓ Already uploaded</Text>
                {(Array.isArray(existingUriValue)
                  ? existingUriValue
                  : [existingUriValue]
                ).map((uri, i) => (
                  <Text key={i} color="green.600" fontSize="11px" mt={0.5} noOfLines={1}>
                    📎 {uri?.split("/").pop()}
                  </Text>
                ))}
              </Box>
            )}

            {/* No file yet */}
            {!currentFile && !hasExisting && (
              <Box mt={2} px={3} py={1} bg="white" borderRadius="6px"
                border="1px dashed" borderColor="gray.300">
                <Text color="gray.400" fontSize="11px">No file selected (optional)</Text>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Error */}
      {error && (
        <Box mt={2} p={2} bg="red.50" borderRadius="6px" border="1px solid" borderColor="red.200">
          <Text color="red.600" fontSize="12px">⚠ {error}</Text>
        </Box>
      )}
    </Box>
  );

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box flex="1" overflowY="auto" overflowX="hidden" pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
        }}>
        <VStack spacing={4} align="stretch">

          {/* Heading */}
          <Text fontSize="17px" fontWeight="600" color="gray.800" textAlign="center">
            Attach Files
          </Text>
          <Text fontSize="13px" color="gray.500" textAlign="center" mt={-2}>
            Upload plot image / registry copy and reference image (both optional)
          </Text>

          {/* ── Zone 1: Plot Image / Registry Copy ── */}
          <UploadZone
            label="Plot Image / Registry Copy"
            subLabel="(optional)"
            icon="🗺️"
            file={plotFile}
            existingUri={existingPlotUri}
            existingUriValue={formData?.attachmentUri}
            isUploading={isUploadingPlot}
            uploadError={plotUploadError}
            inputRef={plotFileInputRef}
            onFileChange={handlePlotFile}
            onDrop={handlePlotDrop}
            accentColor="cyan"
          />

          {/* ── Zone 2: Reference Image ── */}
          <UploadZone
            label="Reference Image"
            subLabel="(optional)"
            icon="🖼️"
            file={refFile}
            existingUri={existingRefUri}
            existingUriValue={formData?.referenceImage}
            isUploading={isUploadingRef}
            uploadError={refUploadError}
            inputRef={refFileInputRef}
            onFileChange={handleRefFile}
            onDrop={handleRefDrop}
            accentColor="purple"
          />

        </VStack>
      </Box>

      {/* Navigation Buttons */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50", borderColor: "cyan.600" }}
          isDisabled={isUploading}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleNext}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          isLoading={isUploading}
          loadingText="Uploading..."
          isDisabled={isNextDisabled}
          _hover={{ bg: "cyan.600" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}